import {
    mdiAccountPlusOutline,
    mdiAccountRemoveOutline,
    mdiCheck,
    mdiChevronDown,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useMemo, useState } from 'react';
import { Popover, Spinner, Variables } from '../base';
import * as S from './assignee-picker.styles';

export interface IAssigneeOption {
    id: string;
    name: string;
    role?: string;
    isYou?: boolean;
    color: string;
}

export interface IAssigneePickerProps {
    selectedIds?: string[];
    options: IAssigneeOption[];
    loading?: boolean;
    saving?: boolean;
    disabled?: boolean;
    onToggle: (userId: string) => void | Promise<void>;
    onClear?: () => void | Promise<void>;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

const compareAssigneeName = (a: IAssigneeOption, b: IAssigneeOption): number =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

export const AssigneePicker: React.FC<IAssigneePickerProps> = ({
    selectedIds = [],
    options,
    loading = false,
    saving = false,
    disabled = false,
    onToggle,
    onClear,
}) => {
    const [open, setOpen] = useState(false);
    const busy = loading || saving || disabled;
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const sortedOptions = useMemo(() => {
        const byId = new Map(options.map(option => [option.id, option]));
        const merged = [...options];
        selectedIds.forEach(id => {
            if (!byId.has(id)) {
                merged.push({
                    id,
                    name: id,
                    color: assigneeAvatarColor(id),
                });
            }
        });
        return merged.sort(compareAssigneeName);
    }, [options, selectedIds]);

    const selected = useMemo(
        () => sortedOptions.filter(option => selectedSet.has(option.id)),
        [sortedOptions, selectedSet],
    );

    const handleToggle = async (userId: string) => {
        if (busy) {
            return;
        }
        await onToggle(userId);
    };

    const handleClear = async () => {
        if (busy || selected.length === 0) {
            return;
        }
        setOpen(false);
        await onClear?.();
    };

    const triggerLabel = (() => {
        if (selected.length === 0) {
            return 'Unassigned';
        }
        const first = selected[0];
        const firstLabel = first.isYou ? 'You' : firstName(first.name);
        if (selected.length === 1) {
            return firstLabel;
        }
        return `${firstLabel} +${selected.length - 1}`;
    })();

    return (
        <Popover
            open={open}
            onOpenChange={next => {
                if (!disabled) {
                    setOpen(next);
                }
            }}
            align="right"
            verticalAlign="auto"
            renderButton={() => (
                <S.Trigger
                    type="button"
                    variant="unstyled"
                    disabled={disabled}
                    aria-label="Change who this task is assigned to">
                    {selected.length === 0 ? (
                        <S.EmptyAvatar>
                            <Icon path={mdiAccountPlusOutline} size={0.55} color={Variables.ThemeLight.TextMild} />
                        </S.EmptyAvatar>
                    ) : (
                        <S.AvatarStack>
                            {selected.slice(0, 3).map(option => (
                                <S.Avatar key={option.id} bg={option.color} isYou={option.isYou} size={22}>
                                    {getInitials(option.name)}
                                </S.Avatar>
                            ))}
                        </S.AvatarStack>
                    )}
                    <S.TriggerLabel>{triggerLabel}</S.TriggerLabel>
                    <Icon path={mdiChevronDown} size={0.65} color={Variables.ThemeLight.TextMild} />
                </S.Trigger>
            )}>
            <S.Menu>
                <S.MenuHeader>
                    Assign to
                    {saving && !loading ? ' · Saving' : ''}
                </S.MenuHeader>
                {loading && (
                    <S.LoadingRow>
                        <Spinner size="16px" />
                    </S.LoadingRow>
                )}
                {!loading && (
                    <S.Options>
                        {sortedOptions.map(option => {
                            const isSelected = selectedSet.has(option.id);
                            return (
                                <S.OptionButton
                                    key={option.id}
                                    type="button"
                                    selected={isSelected}
                                    disabled={busy}
                                    onClick={() => handleToggle(option.id)}>
                                    <S.Avatar bg={option.color} isYou={option.isYou} size={24}>
                                        {getInitials(option.name)}
                                    </S.Avatar>
                                    <S.OptionText>
                                        <S.OptionName>
                                            {option.name}
                                            {option.isYou ? ' (you)' : ''}
                                        </S.OptionName>
                                        {option.role && <S.OptionRole>{option.role}</S.OptionRole>}
                                    </S.OptionText>
                                    {isSelected && (
                                        <Icon
                                            path={mdiCheck}
                                            size={0.7}
                                            color={Variables.ThemeLight.PrimaryConstant6}
                                        />
                                    )}
                                </S.OptionButton>
                            );
                        })}
                        {sortedOptions.length === 0 && (
                            <S.OptionButton type="button" disabled>
                                <S.OptionName>No users</S.OptionName>
                            </S.OptionButton>
                        )}
                        {selected.length > 0 && onClear && (
                            <S.UnassignButton type="button" disabled={busy} onClick={() => handleClear()}>
                                <Icon
                                    path={mdiAccountRemoveOutline}
                                    size={0.7}
                                    color={Variables.ThemeLight.TextMild}
                                />
                                Unassign
                            </S.UnassignButton>
                        )}
                    </S.Options>
                )}
            </S.Menu>
        </Popover>
    );
};

/** Stable palette for assignee avatars (matches redesign mock tones). */
export const assigneeAvatarColor = (seed: string): string => {
    const palette = [
        Variables.ThemeLight.PrimaryConstant6,
        Variables.ThemeLight.SuccessConstant6,
        Variables.ThemeLight.AttentionConstant6,
        Variables.ThemeLight.TextMild,
        Variables.ThemeLight.WarningConstant6,
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return palette[Math.abs(hash) % palette.length];
};

export const formatAssigneeRole = (roleId?: string): string | undefined => {
    if (!roleId) {
        return undefined;
    }
    return roleId
        .toLowerCase()
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};
